import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

/* V5 — route the secondary header utility to the draw generator without
 * rewriting its visible label in JS. CSS owns the label, so Horizon hydration
 * cannot flash it back to “Quiz”. The red quiz CTA is explicitly excluded. */
const secretSantaGeneratorPath = '/pages/generateur-secret-santa';
const secretSantaQuizPath = '/pages/quiz-cadeau-secret-santa';
const secretSantaDrawSelector = `#header-component a[href="${secretSantaQuizPath}"]:not(.header-actions__quiz-cta), #header-component a[href="${secretSantaGeneratorPath}"]:not(.header-actions__quiz-cta), #header-component a.ss-header-draw-link`;

const syncSecretSantaDrawLinks = () => {
  const isGenerator = window.location.pathname === secretSantaGeneratorPath;

  document.querySelectorAll(secretSantaDrawSelector).forEach((link) => {
    link.setAttribute('href', secretSantaGeneratorPath);
    link.classList.add('ss-header-draw-link');

    if (isGenerator) {
      link.setAttribute('aria-current', 'page');
      if (link.classList.contains('menu-list__link')) link.classList.add('menu-list__link--active');
      if (link.classList.contains('menu-drawer__menu-item')) link.classList.add('menu-drawer__menu-item--active');
    }
  });
};

const getSecretSantaDrawLink = (target) => {
  if (!(target instanceof Element)) return null;
  const link = target.closest('a');
  if (!link || !link.closest('#header-component') || link.classList.contains('header-actions__quiz-cta')) return null;
  const href = link.getAttribute('href');
  if (href !== secretSantaQuizPath && href !== secretSantaGeneratorPath && !link.classList.contains('ss-header-draw-link')) return null;
  return link;
};

syncSecretSantaDrawLinks();
window.addEventListener('pageshow', syncSecretSantaDrawLinks);
document.addEventListener('shopify:section:load', syncSecretSantaDrawLinks);

document.addEventListener(
  'pointerdown',
  (event) => {
    const link = getSecretSantaDrawLink(event.target);
    if (link) link.setAttribute('href', secretSantaGeneratorPath);
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const link = getSecretSantaDrawLink(event.target);
    if (!link) return;
    if (link.getAttribute('href') !== secretSantaGeneratorPath) link.setAttribute('href', secretSantaGeneratorPath);
  },
  true
);
